// src/lib/demoData.ts
//
// In-memory demo data for the BENCHMARK site (Day 3 + 4).
// This is the data layer for the catalog, stores, and approval
// queue — synchronous, deterministic, no backend needed yet.
//
// IMAGES:
//   All product images are real vendor photos downloaded and
//   stored under /public/products/. File extensions vary per
//   product (some are .jpg from Magnepan, some are .png from
//   Shopify-hosted vendors). The TS build reads them as static
//   imports; we use plain string paths so Webpack's public asset
//   pipeline handles them.
//   When the FastAPI backend is wired in (Day 5+), this file stays
//   as a fallback. TanStack Query should fetch live data from the
//   API instead.

/**
 * Empty module-level constant kept around for backwards compat.
 * The real lookup now lives in productImageUrl(sku).
 */
const PRODUCT_IMG_BASE = "/products";

export function productImageUrl(sku: string): string {
  // For now we hardcode the extension mapping. In prod this would
  // come from a manifest served by the CDN: GET /product-images.json
  const mapping: Record<string, string> = {
    "VAL-PROJ-2025": "jpg",
    "THEATRON-MAG15": "jpg",
    "DREAMSCREEN-XL": "jpg",
    "SONITUS-RAS-12": "jpg",
    "SONITUS-DIFF-32": "jpg",
    "RV-PRO-8K": "jpg",
    "SOFAB-X1": "jpg",
    "MAGNETAR-UDP900": "png",
    "MADV-ENVY": "png",
    "MADVR-ENVY-MK2": "jpg",
    "AETHERION-MX2": "png",
    "ROLLATV-85PRO": "jpg",
    "DAC-REF2": "png",
    "RV-PRO-8K-EVO": "jpg",
  };
  const ext = mapping[sku];
  if (!ext) return `${PRODUCT_IMG_BASE}/placeholder.jpg`;
  return `${PRODUCT_IMG_BASE}/${sku}.${ext}`;
}

export type Category = "video" | "audio" | "screens" | "acoustic" | "accessories";

export interface Product {
  sku: string;
  name: string;
  /** Canonical vendor display name (Shop-owned enrichment) — preferred
   * headline over FileMaker's raw full_name when present. */
  displayName?: string | null;
  brand: string;
  category: Category;
  /** Real taxonomy label (migration 024) — wins over the legacy derived
   * bucket chip when set; localized en/fr/nl. */
  taxonomyLabel?: { slug: string; en: string; fr: string; nl: string } | null;
  description: string;
  priceEUR: number;
  currency: "EUR" | "USD" | "GBP";
  /** Main hero image — small but high-impact for the catalog card. */
  image: string;
  /**
   * Gallery of additional product shots, shown on the detail page
   * as a thumb carousel. Used for in-room shots, back-of-unit,
   * exploded view, etc. Each entry is a URL/path that's reachable
   * as a static asset (same convention as `image`).
   *
   * Currently populated from vendor sites + dealer galleries at
   * build time. Future: populated by the catalog-builder agent
   * in Week 5+, uploaded to S3 + served via CloudFront.
   */
  images: string[];
  specs: Array<{ key: string; value: string }>;
  tags: string[];
  /** null means unverified availability, not out of stock. */
  inStock: boolean | null;
  /** Live catalog products remain UNKNOWN until a coverage policy is approved. */
  stockStatus?: "UNKNOWN" | undefined;
  featured: boolean;
  updatedAt: string;
  approved: "approved" | "pending" | "rejected";
  /**
   * Wholesale price tier (admin-visible).
   * End customers see tiered pricing in Day 5 portal.
   */
  pricingTier?: "standard" | "preferred" | "vip" | undefined;
  vendorNotes?: string | undefined;
  /** Family size-variant table (migration 025): every catalogue
   * combination of the product's family, browsable on the product page.
   * Null/absent when the product has no variant table. */
  variantTable?: VariantTableData | null | undefined;
}

/** Family size-combination table attached to a product (migration 025).
 * rows: aspect-ratio key -> list of combinations (col -> value).
 * A row may also carry list_price_by_fabric (Record<fabricCode, EUR>)
 * alongside the scalar cells — vendor MSRP matrix, not displayed as a
 * column. */
export interface VariantTableData {
  key: string;
  title: string;
  columns: string[];
  column_labels: Record<string, { en: string; fr: string; nl: string; unit: string | null }>;
  aspect_labels: Record<string, string>;
  rows: Record<string, Array<Record<string, number | string | Record<string, number>>>>;
  source: string;
  current: { aspect: string; viewing_width_mm: number } | null;
}

export interface PartnerShop {
  id: string;
  name: string;
  address: string[];
  city: string;
  country: string;
  phone: string;
  email: string;
  hours: Array<{ day: string; hours: string }>;
  carries: Category[];
}

export interface ApprovalItem {
  id: string;
  type: "product-create" | "product-update" | "price-update";
  sku: string;
  title: string;
  submittedBy: string;
  submittedAt: string;
  risk: "low" | "medium" | "high";
  summary: string;
  /**
   * Required when an admin rejects this item.
   * Captured by the audit log so partners can see why their
   * submission was sent back.
   */
  rejectionReason?: string | undefined;
  /**
   * ISO timestamp when this approval was actioned (approved or rejected).
   * Used to render the recent activity feed on the dashboard.
   */
  decidedAt?: string | undefined;
  /**
   * The admin who decided this item. Empty for items still pending.
   */
  decidedBy?: string | undefined;
}

/**
 * Append-only audit entry. Persisted to the activity log on the dashboard
 * when an admin approves / rejects / edits anything.
 */
export interface AuditEntry {
  id: string;
  ts: string;
  actor: string;
  /** What the actor did */
  action:
    | "approve"
    | "reject"
    | "create-product"
    | "update-product"
    | "delete-product"
    | "approve-product-draft"
    | "import-from-agent"
    | "mirror-batch"
    | "writeback"
    | "anomaly-detected"
    | "schema-drift"
    | "settings-update"
    | "settings-reset"
    | "incentive-advance";
  /** Target SKU (if applicable) */
  sku?: string | undefined;
  /** Short description for the dashboard feed */
  summary: string;
}

export const DEMO_PRODUCTS: Product[] = [
  {
    sku: "VAL-PROJ-2025",
    name: "Valerion ProVision 2025",
    brand: "Valerion",
    category: "video",
    description: "A high-end home cinema projector with 4K HDR, 3000 lumens, and motorized lens shift.",
    priceEUR: 8990,
    currency: "EUR",
    image: "/products/VAL-PROJ-2025.jpg",
    images: [],
    specs: [
      { key: "Resolution", value: "3840 x 2160 (4K)" },
      { key: "Brightness", value: "3000 ANSI lumens" },
      { key: "HDR", value: "HDR10+, Dolby Vision" },
      { key: "Lamp life", value: "20,000 hours" },
      { key: "Throw ratio", value: "1.5:1 - 2.1:1" },
    ],
    tags: ["4K", "HDR", "home cinema", "projector"],
    inStock: true,
    featured: true,
    updatedAt: "2026-06-15T10:00:00Z",
    approved: "approved",
  },
  {
    sku: "THEATRON-MAG15",
    name: "MAG Theatron 15 (L/C/R Speaker)",
    brand: "MAG Theatron",
    category: "audio",
    description: "Professional-grade L/C/R speaker for home cinema. 3-way design with dual 15 inch woofers.",
    priceEUR: 4500,
    currency: "EUR",
    image: "/products/THEATRON-MAG15.jpg",
    images: ["/products/THEATRON-MAG15-2.jpg"],
    specs: [
      { key: "Configuration", value: "3-way, sealed enclosure" },
      { key: "Drivers", value: "2x 15 inch woofers, horn mid, 1.4 inch tweeter" },
      { key: "Sensitivity", value: "98 dB / 2.83 V / 1 m" },
      { key: "Impedance", value: "8 ohms" },
      { key: "Power handling", value: "800 W RMS" },
    ],
    tags: ["speaker", "L/C/R", "high-end", "studio"],
    inStock: true,
    featured: true,
    updatedAt: "2026-06-12T08:30:00Z",
    approved: "approved",
  },
  {
    sku: "DREAMSCREEN-XL",
    name: 'DreamScreen XL (150" ALR)',
    brand: "DreamScreen",
    category: "screens",
    description: 'Ambient-light-rejecting fixed-frame screen with 150" diagonal. Optimized for laser projectors.',
    priceEUR: 3200,
    currency: "EUR",
    image: "/products/DREAMSCREEN-XL.jpg",
    images: ["/products/DREAMSCREEN-XL-2.jpg"],
    specs: [
      { key: "Diagonal", value: "150 inches" },
      { key: "Gain", value: "0.85" },
      { key: "Frame", value: "Aluminum, 12 mm" },
      { key: "Surface", value: "ALR 0.85 Cinegrey 3D" },
      { key: "Mounting", value: "Wall, fixed" },
    ],
    tags: ["screen", "ALR", "150 inch"],
    inStock: true,
    featured: true,
    updatedAt: "2026-06-20T14:00:00Z",
    approved: "approved",
  },
  {
    sku: "SONITUS-RAS-12",
    name: "Sonitus RAS-12 (Acoustic Panel)",
    brand: "Sonitus",
    category: "acoustic",
    description: "Slim-line acoustic absorber panel for first-reflection points.",
    priceEUR: 290,
    currency: "EUR",
    image: "/products/SONITUS-RAS-12.jpg",
    images: ["/products/SONITUS-RAS-12-2.jpg"],
    specs: [
      { key: "Material", value: "MDF + acoustic foam" },
      { key: "Thickness", value: "50 mm" },
      { key: "Dimensions", value: "1200 x 600 mm" },
      { key: "Color", value: "White, paintable" },
      { key: "Mounting", value: "Wall, adhesive + z-clip" },
    ],
    tags: ["acoustic", "panel", "treatment"],
    inStock: true,
    featured: false,
    updatedAt: "2026-05-30T11:00:00Z",
    approved: "approved",
  },
  {
    sku: "RV-PRO-8K",
    name: "R_Volution Pro 8K Media Player",
    brand: "R_Volution",
    category: "video",
    description: "8K HDR media player with 16 TB storage and dual HDMI outputs.",
    priceEUR: 5400,
    currency: "EUR",
    image: "/products/RV-PRO-8K.jpg",
    images: ["/products/RV-PRO-8K-2.jpg"],
    specs: [
      { key: "Output", value: "8K HDR, dual HDMI 2.1" },
      { key: "Storage", value: "16 TB HDD" },
      { key: "Codecs", value: "AV1, HEVC, VP9" },
      { key: "Network", value: "2.5 GbE" },
      { key: "Control", value: "RS-232, IP, IR" },
    ],
    tags: ["media player", "8K", "automation"],
    inStock: true,
    featured: true,
    updatedAt: "2026-06-10T09:00:00Z",
    approved: "approved",
  },
  {
    sku: "SOFAB-X1",
    name: "Sofabaton X1 (Universal Remote)",
    brand: "Sofabaton",
    category: "accessories",
    description: 'Universal remote with 2.4" LCD and 1-mile RF range.',
    priceEUR: 99,
    currency: "EUR",
    image: "/products/SOFAB-X1.jpg",
    images: ["/products/SOFAB-X1-2.jpg"],
    specs: [
      { key: "Range", value: "1.6 km (RF)" },
      { key: "Display", value: '2.4" color LCD' },
      { key: "Battery", value: "Rechargeable, 30 days" },
      { key: "Devices", value: "Up to 30 devices" },
      { key: "Connectivity", value: "Wi-Fi, Bluetooth, IR" },
    ],
    tags: ["remote", "automation", "wifi"],
    inStock: true,
    featured: false,
    updatedAt: "2026-06-25T15:30:00Z",
    approved: "approved",
  },
  {
    sku: "MAGNETAR-UDP900",
    name: "Magnetar UDP900 (4K Blu-ray)",
    brand: "Magnetar",
    category: "video",
    description: "Reference 4K UHD Blu-ray player with Dolby Vision and HDR10+.",
    priceEUR: 2499,
    currency: "EUR",
    image: "/products/MAGNETAR-UDP900.png",
    images: ["/products/MAGNETAR-UDP900-front.png", "/products/MAGNETAR-UDP900-2.jpg"],
    specs: [
      { key: "Disc support", value: "4K UHD, Blu-ray, SACD, DVD-Audio" },
      { key: "HDR", value: "Dolby Vision, HDR10+, SL-HDR1" },
      { key: "DAC", value: "ESS Sabre, dual differential" },
      { key: "Chassis", value: "Aluminum, double-shielded" },
      { key: "Output", value: "2 x HDMI, balanced XLR" },
    ],
    tags: ["bluray", "player", "audiophile"],
    inStock: true,
    featured: false,
    updatedAt: "2026-06-22T12:00:00Z",
    approved: "approved",
  },
  {
    sku: "MADV-ENVY",
    name: "madVR Envy (Video Processor)",
    brand: "madVr",
    category: "video",
    description: "AI-driven video processor used in commercial cinema and high-end home theaters.",
    priceEUR: 7990,
    currency: "EUR",
    image: "/products/MADV-ENVY.png",
    images: ["/products/MADV-ENVY-2.jpg", "/products/MADV-ENVY-3.jpg"],
    specs: [
      { key: "Output", value: "4K 120 Hz, 8K 60 Hz" },
      { key: "Processing", value: "Neural network AI" },
      { key: "Tone mapping", value: "Dynamic per-frame" },
      { key: "Chassis", value: "Rack-mount 2U" },
      { key: "Control", value: "IP, RS-232, mobile app" },
    ],
    tags: ["video processor", "AI", "high-end"],
    inStock: false,
    featured: true,
    updatedAt: "2026-06-05T16:00:00Z",
    approved: "approved",
  },
  {
    sku: "AETHERION-MX2",
    name: "Aetherion MX2 (Amplifier)",
    brand: "Aetherion",
    category: "audio",
    description: "Reference 2-channel power amplifier. 200 W per channel into 8 ohms. Zero-feedback discrete design.",
    priceEUR: 12500,
    currency: "EUR",
    image: "/products/AETHERION-MX2.png",
    images: [],
    specs: [
      { key: "Power", value: "2 x 200 W (8 ohms)" },
      { key: "Topology", value: "Zero feedback, class A/AB" },
      { key: "Inputs", value: "Balanced XLR, single-ended RCA" },
      { key: "Frequency response", value: "5 Hz - 100 kHz" },
      { key: "SNR", value: "120 dB" },
    ],
    tags: ["amplifier", "audiophile", "2-channel"],
    inStock: true,
    featured: false,
    updatedAt: "2026-06-18T10:30:00Z",
    approved: "approved",
  },
  {
    sku: "ROLLATV-85PRO",
    name: 'RollaTv 85 Pro (Display)',
    brand: "RollaTv",
    category: "screens",
    description: 'Professional 85" display optimized for home cinema with 1500 nits and 120 Hz.',
    priceEUR: 6500,
    currency: "EUR",
    image: "/products/ROLLATV-85PRO.jpg?v=2",
    images: ["/products/ROLLATV-85PRO-2.jpg", "/products/ROLLATV-85PRO-3.jpg", "/products/ROLLATV-85PRO-4.jpg"],
    specs: [
      { key: "Diagonal", value: "85 inches" },
      { key: "Brightness", value: "1500 nits peak" },
      { key: "Refresh", value: "120 Hz native" },
      { key: "HDR", value: "HDR10+, Dolby Vision IQ" },
      { key: "Inputs", value: "4 x HDMI 2.1, 2 x USB" },
    ],
    tags: ["display", "85 inch", "120hz"],
    inStock: true,
    featured: false,
    updatedAt: "2026-06-08T13:00:00Z",
    approved: "approved",
  },
  {
    sku: "SONITUS-DIFF-32",
    name: "Sonitus Diffusor 32",
    brand: "Sonitus",
    category: "acoustic",
    description: "Quadratic diffusor that scatters reflections evenly without absorbing them.",
    priceEUR: 380,
    currency: "EUR",
    image: "/products/SONITUS-DIFF-32.jpg",
    images: ["/products/SONITUS-DIFF-32-2.jpg"],
    specs: [
      { key: "Material", value: "High-density polystyrene" },
      { key: "Color", value: "White, paintable" },
      { key: "Dimensions", value: "800 x 800 mm" },
      { key: "Depth", value: "150 mm" },
      { key: "Mounting", value: "Wall, glue" },
    ],
    tags: ["acoustic", "diffusor", "treatment"],
    inStock: true,
    featured: false,
    updatedAt: "2026-05-28T09:30:00Z",
    approved: "approved",
  },
  {
    sku: "DAC-REF2",
    name: "DAC Reference 2",
    brand: "DAC",
    category: "audio",
    description: "Reference stereo DAC with ESS Pro chipset and dual crystal oscillators.",
    priceEUR: 5800,
    currency: "EUR",
    image: "/products/DAC-REF2.png",
    images: ["/products/DAC-REF2-2.png"],
    specs: [
      { key: "Chipset", value: "ESS Sabre ES9038PRO" },
      { key: "DSD support", value: "Up to DSD1024" },
      { key: "PCM", value: "32 bit / 768 kHz" },
      { key: "Outputs", value: "Balanced XLR, RCA, headphone" },
      { key: "Inputs", value: "USB, AES/EBU, optical, coaxial" },
    ],
    tags: ["DAC", "audiophile"],
    inStock: true,
    featured: false,
    updatedAt: "2026-06-14T11:00:00Z",
    approved: "approved",
  },
  // Pending products: in admin queue only, not on public catalog
  {
    sku: "RV-PRO-8K-EVO",
    name: "R_Volution Pro 8K Evo",
    brand: "R_Volution",
    category: "video",
    description: "New Evo variant of the Pro 8K with faster ARM CPU and 22 TB storage.",
    priceEUR: 7400,
    currency: "EUR",
    image: "/products/RV-PRO-8K-EVO.jpg",
    images: ["/products/RV-PRO-8K-2.jpg"],
    specs: [
      { key: "Storage", value: "22 TB HDD" },
      { key: "CPU", value: "ARM Cortex-A76" },
      { key: "Chassis", value: '1U 19" rack-mount' },
    ],
    tags: ["media player", "8K Evo", "rack"],
    inStock: false,
    featured: false,
    updatedAt: "2026-07-03T09:15:00Z",
    approved: "pending",
  },
  {
    sku: "MADVR-ENVY-MK2",
    name: "madVR Envy MK2",
    brand: "madVr",
    category: "video",
    description: "Updated madVR Envy with new black chassis option and improved AI model.",
    priceEUR: 8990,
    currency: "EUR",
    image: "/products/MADVR-ENVY-MK2.jpg",
    images: ["/products/MADVR-ENVY-MK2-2.jpg"],
    specs: [
      { key: "Color", value: "Black (new option)" },
      { key: "AI model", value: "v2.3 (improved tone mapping)" },
      { key: "Availability", value: "3 weeks" },
    ],
    tags: ["video processor", "MK2", "high-end"],
    inStock: false,
    featured: false,
    updatedAt: "2026-07-04T16:48:00Z",
    approved: "pending",
  },
];

export const DEMO_PARTNER_SHOPS: PartnerShop[] = [
  {
    id: "BENCHMARK-NAMUR",
    name: "BENCHMARK AV (Namur HQ)",
    address: ["256, Chaussee de Marche", "Batiment B"],
    city: "5100 Jambes, Belgium",
    country: "BE",
    phone: "+32 81 22 41 49",
    email: "namur@benchmark-av.be",
    hours: [
      { day: "Monday", hours: "Closed" },
      { day: "Tue - Fri", hours: "10:00 - 18:00" },
      { day: "Saturday", hours: "10:00 - 17:00" },
      { day: "Sunday", hours: "Closed" },
    ],
    carries: ["video", "audio", "screens", "acoustic", "accessories"],
  },
  {
    id: "CINERAMA-PARIS",
    name: "Cinerama Paris",
    address: ["47, rue de Richelieu"],
    city: "75001 Paris, France",
    country: "FR",
    phone: "+33 1 42 96 12 87",
    email: "paris@cinorama.fr",
    hours: [
      { day: "Mon - Sat", hours: "10:00 - 19:00" },
      { day: "Sunday", hours: "Closed" },
    ],
    carries: ["video", "audio", "screens"],
  },
  {
    id: "STUDIO-NORDEN-AMSTERDAM",
    name: "Studio Norden (Amsterdam)",
    address: ["Prinsengracht 263"],
    city: "1016 GV Amsterdam, Netherlands",
    country: "NL",
    phone: "+31 20 624 18 96",
    email: "amsterdam@studionorden.nl",
    hours: [
      { day: "Mon - Fri", hours: "09:00 - 18:00" },
      { day: "Saturday", hours: "10:00 - 16:00" },
      { day: "Sunday", hours: "Closed" },
    ],
    carries: ["audio", "acoustic", "accessories"],
  },
  {
    id: "ACOUSTIC-BRUSSELS",
    name: "Acoustic Brussels",
    address: ["Chaussee d'Ixelles 145"],
    city: "1050 Bruxelles, Belgium",
    country: "BE",
    phone: "+32 2 538 49 11",
    email: "info@acoustic-brussels.be",
    hours: [
      { day: "Tue - Sat", hours: "11:00 - 18:30" },
      { day: "Mon", hours: "By appointment" },
      { day: "Sunday", hours: "Closed" },
    ],
    carries: ["acoustic", "audio"],
  },
  {
    id: "KINO-LUXEMBURG",
    name: "Kino Luxemburg",
    address: ["12, avenue Monterey"],
    city: "2163 Luxembourg, Luxembourg",
    country: "LU",
    phone: "+352 22 86 60",
    email: "contact@kino-lux.lu",
    hours: [
      { day: "Mon - Fri", hours: "10:00 - 18:00" },
      { day: "Saturday", hours: "10:00 - 14:00" },
      { day: "Sunday", hours: "Closed" },
    ],
    carries: ["video", "audio", "screens"],
  },
  {
    id: "HIFI-COLOGNE",
    name: "HiFi Cologne",
    address: ["Ehrenstrasse 51"],
    city: "50672 Koln, Germany",
    country: "DE",
    phone: "+49 221 12 38 45",
    email: "info@hifi-cologne.de",
    hours: [
      { day: "Mon - Fri", hours: "10:00 - 19:00" },
      { day: "Saturday", hours: "10:00 - 18:00" },
      { day: "Sunday", hours: "Closed" },
    ],
    carries: ["audio", "accessories"],
  },
];

// ----------------------------------------------------------------------------
// Day 5: Professional Portal
// ----------------------------------------------------------------------------

/**
 * Wholesale pricing tier for a customer. The discount is applied
 * PER ITEM to the catalog list price (priceEUR). Negative-deltas are
 * shown as "—" in the portal so the customer knows it's a quote-only item.
 */
export type PricingTier = "standard" | "preferred" | "vip" | "quote";

/** Per-tier discount (0.15 = 15% off list price). */
export const TIER_DISCOUNT: Record<PricingTier, number> = {
  standard: 0,
  preferred: 0.08,
  vip: 0.15,
  quote: -1, // -1 sentinel = contact-for-quote only
};

/**
 * One Customer account that can sign in to the /portal/* section
 * (per M28 + week-1 plan Day 5).
 *
 * Wiring:
 *   - In production, this maps 1-to-1 with a Keycloak user that has
 *     groups including `bench-customer-<id>`. We use a single
 *     `customerId` field for the demo so the same data works
 *     without a backend.
 */
export interface CustomerAccount {
  id: string;
  companyName: string;
  contactName: string;
  email: string;
  country: string;
  pricingTier: PricingTier;
  /** Maximum monthly spend they have on file (EUR). 0 = unlimited. */
  monthlyLimitEUR: number;
  /** Free-form shipping address. */
  shipTo: string[];
  activeSince: string;
}

/**
 * One line item inside an order / cart.
 * (image is captured at time of order so we don't depend on the
 *  current product state).
 */
export interface OrderLine {
  sku: string;
  name: string;
  brand: string;
  image: string;
  unitPriceEUR: number;
  /** Effective unit price after tier discount (what they paid). */
  netUnitPriceEUR: number;
  qty: number;
  /** Snapshot of the discount applied (e.g. 0.15 for 15% off). */
  tierDiscount: number;
}

/**
 * Status lifecycle of a B2B order:
 *   draft     -> only saved in /portal, not submitted
 *   submitted -> admin sees it, not yet approved
 *   approved  -> admin approved, awaiting warehouse
 *   shipped   -> warehouse has handed off to courier
 *   delivered -> customer signed for it
 *   cancelled -> at any point before shipped
 */
export type OrderStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "shipped"
  | "delivered"
  | "cancelled";

export interface Order {
  id: string;
  customerId: string;
  placedAt: string;
  status: OrderStatus;
  lines: OrderLine[];
  /** Optional internal notes for the warehouse team. */
  notes?: string | undefined;
  /** When the order moved into each status (audit trail). */
  statusHistory?: Array<{ status: OrderStatus; at: string; actor?: string }> | undefined;
}

// ----------------------------------------------------------------------------
// Sample data
// ----------------------------------------------------------------------------

export const DEMO_CUSTOMERS: CustomerAccount[] = [
  {
    id: "C-1001",
    companyName: "Studio Norden",
    contactName: "Anders Holmlund",
    email: "anders@studionorden.se",
    country: "SE",
    pricingTier: "vip",
    monthlyLimitEUR: 50_000,
    shipTo: ["Studio Norden", "Prinsengracht 263", "1016 GV Amsterdam", "Netherlands"],
    activeSince: "2024-02-11",
  },
  {
    id: "C-1002",
    companyName: "Cinema Royale",
    contactName: "Bernard Dubois",
    email: "bernard@cinema-royale.fr",
    country: "FR",
    pricingTier: "preferred",
    monthlyLimitEUR: 25_000,
    shipTo: ["Cinema Royale", "47, rue de Richelieu", "75001 Paris", "France"],
    activeSince: "2024-09-04",
  },
  {
    id: "C-1003",
    companyName: "SoundHouse",
    contactName: "Eva Lindgren",
    email: "eva@soundhouse.dk",
    country: "DK",
    pricingTier: "standard",
    monthlyLimitEUR: 10_000,
    shipTo: ["SoundHouse ApS", "Ehrenstrasse 51", "50672 Köln", "Germany"],
    activeSince: "2025-01-20",
  },
];

export const DEMO_ORDERS: Order[] = [
  {
    id: "ORD-2026-0017",
    customerId: "C-1001",
    placedAt: "2026-07-04T11:32:00Z",
    status: "shipped",
    lines: [
      {
        sku: "MADV-ENVY",
        name: "madVR Envy (Video Processor)",
        brand: "madVr",
        image: "/products/MADV-ENVY.png",
        unitPriceEUR: 7990,
        netUnitPriceEUR: 7990 * 0.85,
        qty: 1,
        tierDiscount: 0.15,
      },
      {
        sku: "ROLLATV-85PRO",
        name: "RollaTv 85 Pro (Display)",
        brand: "RollaTv",
        image: "/products/ROLLATV-85PRO.jpg",
        unitPriceEUR: 6500,
        netUnitPriceEUR: 6500 * 0.85,
        qty: 2,
        tierDiscount: 0.15,
      },
    ],
    notes: "Customer requested white-glove delivery.",
    statusHistory: [
      { status: "submitted", at: "2026-07-04T11:32:00Z", actor: "anders@studionorden.se" },
      { status: "approved", at: "2026-07-04T13:10:00Z", actor: "admin@benchmark-av.be" },
      { status: "shipped", at: "2026-07-05T08:45:00Z", actor: "warehouse" },
    ],
  },
  {
    id: "ORD-2026-0016",
    customerId: "C-1001",
    placedAt: "2026-07-01T16:08:00Z",
    status: "delivered",
    lines: [
      {
        sku: "THEATRON-MAG15",
        name: "MAG Theatron 15 (L/C/R Speaker)",
        brand: "MAG Theatron",
        image: "/products/THEATRON-MAG15.jpg",
        unitPriceEUR: 4500,
        netUnitPriceEUR: 4500 * 0.85,
        qty: 3,
        tierDiscount: 0.15,
      },
    ],
    notes: "Splitter cable included.",
    statusHistory: [
      { status: "submitted", at: "2026-07-01T16:08:00Z", actor: "anders@studionorden.se" },
      { status: "approved", at: "2026-07-01T17:25:00Z", actor: "admin@benchmark-av.be" },
      { status: "shipped", at: "2026-07-02T11:00:00Z", actor: "warehouse" },
      { status: "delivered", at: "2026-07-04T09:32:00Z", actor: "courier" },
    ],
  },
  {
    id: "ORD-2026-0015",
    customerId: "C-1002",
    placedAt: "2026-06-28T09:14:00Z",
    status: "delivered",
    lines: [
      {
        sku: "SOFAB-X1",
        name: "Sofabaton X1 (Universal Remote)",
        brand: "Sofabaton",
        image: "/products/SOFAB-X1.jpg",
        unitPriceEUR: 99,
        netUnitPriceEUR: 99 * 0.92,
        qty: 12,
        tierDiscount: 0.08,
      },
      {
        sku: "DREAMSCREEN-XL",
        name: 'DreamScreen XL (150" ALR)',
        brand: "DreamScreen",
        image: "/products/DREAMSCREEN-XL.jpg",
        unitPriceEUR: 3200,
        netUnitPriceEUR: 3200 * 0.92,
        qty: 1,
        tierDiscount: 0.08,
      },
    ],
    statusHistory: [
      { status: "submitted", at: "2026-06-28T09:14:00Z", actor: "bernard@cinema-royale.fr" },
      { status: "approved", at: "2026-06-28T11:00:00Z", actor: "admin@benchmark-av.be" },
      { status: "shipped", at: "2026-06-29T14:00:00Z", actor: "warehouse" },
      { status: "delivered", at: "2026-07-02T15:30:00Z", actor: "courier" },
    ],
  },
  {
    id: "ORD-2026-0014",
    customerId: "C-1003",
    placedAt: "2026-06-25T14:00:00Z",
    status: "submitted",
    lines: [
      {
        sku: "SONITUS-RAS-12",
        name: "Sonitus RAS-12 (Acoustic Panel)",
        brand: "Sonitus",
        image: "/products/SONITUS-RAS-12.jpg",
        unitPriceEUR: 290,
        netUnitPriceEUR: 290,
        qty: 24,
        tierDiscount: 0,
      },
    ],
    notes: "Awaiting studio-norden approval before shipping.",
    statusHistory: [
      { status: "submitted", at: "2026-06-25T14:00:00Z", actor: "eva@soundhouse.dk" },
    ],
  },
];

// Pre-populated audit log entries (demo data).
// In production, these come from the audit trail in Postgres.
export const DEMO_AUDIT_LOG: AuditEntry[] = [
  {
    id: "EVT-001",
    ts: "2026-07-04T16:48:00Z",
    actor: "admin@benchmark-av.be",
    action: "approve-product-draft",
    sku: "RV-PRO-8K-EVO",
    summary: "Approved product draft for RV-PRO-8K-EVO",
  },
  {
    id: "EVT-002",
    ts: "2026-07-04T14:22:00Z",
    actor: "studio-norden@studionorden.nl",
    action: "update-product",
    sku: "AETHERION-MX2",
    summary: "Submitted +8% price increase request",
  },
  {
    id: "EVT-003",
    ts: "2026-07-03T09:15:00Z",
    actor: "philou@cinophil.be",
    action: "create-product",
    sku: "RV-PRO-8K-EVO",
    summary: "Created draft for new product",
  },
];

export const DEMO_APPROVALS: ApprovalItem[] = [
  {
    id: "APR-001",
    type: "product-create",
    sku: "RV-PRO-8K-EVO",
    title: "New SKU: R_Volution Pro 8K Evo",
    submittedBy: "philou@cinophil.be",
    submittedAt: "2026-07-03T09:15:00Z",
    risk: "low",
    summary:
      "New product addition for the upcoming 8K-Evo variant. Wholesale: 5,200 EUR. Same brand and category as existing RV-PRO-8K.",
  },
  {
    id: "APR-002",
    type: "price-update",
    sku: "AETHERION-MX2",
    title: "Price increase: Aetherion MX2 +8%",
    submittedBy: "studio-norden@studionorden.nl",
    submittedAt: "2026-07-04T14:22:00Z",
    risk: "medium",
    summary:
      "Customer requests a price increase from 12,500 EUR to 13,500 EUR. Aetherion HQ has approved but margin drops from 22% to 18%.",
  },
  {
    id: "APR-003",
    type: "product-update",
    sku: "MADVR-ENVY-MK2",
    title: "Update: madVR Envy MK2 + new color",
    submittedBy: "luxembourg@example.lu",
    submittedAt: "2026-07-04T16:48:00Z",
    risk: "low",
    summary:
      "Spec sheet update + a new color option (black). No price impact. Supplier confirms availability in 3 weeks.",
  },
  {
    id: "APR-004",
    type: "product-create",
    sku: "TIK-HOME-FLOOR",
    title: "New SKU: Tik Home Floor Stand",
    submittedBy: "acme@example.be",
    submittedAt: "2026-07-05T08:12:00Z",
    risk: "high",
    summary:
      "New vendor not previously in our catalog. Requires verification of distributor agreement and tax compliance. Sample not yet received.",
  },
];
